import { useEffect, useState, useMemo, useCallback } from "react";
import api from "../services/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  IndianRupee, TrendingUp, Truck, Loader2, Download, FileText,
  CreditCard, Wallet, Users, AlertTriangle, Package, Briefcase, ArrowDown, BarChart3, Wrench,
} from "lucide-react";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";

// ─── Visual constants — kept in sync with the rest of the admin theme. ───
const COST_SPLIT_COLORS = ["#FF6B00", "#2563eb", "#8b5cf6", "#16a34a", "#10b981"];
const METHOD_COLORS = { cod: "#f59e0b", wallet: "#8b5cf6", online: "#16a34a" };
const PAYMENT_LABELS = { cod: "COD", wallet: "Wallet", online: "Online" };

const fmtINR = (n) => `INR ${Math.round(n || 0).toLocaleString()}`;
// Used in the per-order table — when a stored cost field is missing we
// surface "N/A" rather than misleading the admin with a 0.
const fmtCost = (n) => (n === null || n === undefined ? "N/A" : fmtINR(n));

function StatCard({ icon: Icon, label, value, sub, accent = "orange", testId }) {
  const accents = {
    orange: "bg-orange-50 text-[#FF6B00] border-orange-100",
    green: "bg-emerald-50 text-emerald-600 border-emerald-100",
    blue: "bg-blue-50 text-blue-600 border-blue-100",
    red: "bg-red-50 text-red-600 border-red-100",
    purple: "bg-purple-50 text-purple-600 border-purple-100",
    slate: "bg-slate-50 text-slate-600 border-slate-100",
    amber: "bg-amber-50 text-amber-600 border-amber-100",
  };
  return (
    <div className={`bg-white rounded-2xl shadow-sm border ${accents[accent].split(" ")[2]} p-5 card-hover animate-fadeInUp`} data-testid={testId}>
      <div className={`w-10 h-10 ${accents[accent].split(" ").slice(0, 2).join(" ")} rounded-xl flex items-center justify-center mb-3`}>
        <Icon className="h-5 w-5" />
      </div>
      <p className="font-['Outfit'] text-xl font-bold text-slate-900 tracking-tight">{value}</p>
      <p className="text-sm text-slate-500 mt-0.5">{label}</p>
      {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
    </div>
  );
}

function ChartCard({ title, subtitle, children, testId }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 md:p-6 animate-fadeInUp" data-testid={testId}>
      <div className="mb-5">
        <h2 className="font-['Outfit'] text-base font-semibold text-slate-900">{title}</h2>
        {subtitle && <p className="text-xs text-slate-400 mt-0.5">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

function CostTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-900 text-white px-3 py-2 rounded-lg shadow-xl text-xs">
      {label && <p className="font-semibold mb-1">{label}</p>}
      {payload.map((p) => (
        <p key={p.dataKey || p.name} style={{ color: p.color || p.fill }}>
          {p.name}: {fmtINR(p.value)}
        </p>
      ))}
    </div>
  );
}

const STATUS_BADGE = {
  delivered: "bg-emerald-50 text-emerald-700 border-emerald-200",
  cancelled: "bg-red-50 text-red-700 border-red-200",
  refunded: "bg-amber-50 text-amber-700 border-amber-200",
};
const PAYMENT_BADGE = {
  cod: "bg-amber-50 text-amber-700 border-amber-200",
  wallet: "bg-purple-50 text-purple-700 border-purple-200",
  online: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

export default function AdminRevenue() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    start_date: "",
    end_date: "",
    payment_method: "all",
    status: "all",
    zone: "all",
    user_type: "all",
  });

  const load = useCallback(() => {
    setLoading(true);
    const params = Object.fromEntries(
      Object.entries(filters).filter(([, v]) => v && v !== "all")
    );
    api.get("/admin/revenue-analytics", { params })
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const handleExport = (type) => {
    const token = localStorage.getItem("token");
    const base = api.defaults.baseURL || "";
    const url = `${base}/admin/export/revenue-${type}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const u = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = u;
        a.download = `pickngo_revenue.${type}`;
        a.click();
        URL.revokeObjectURL(u);
      });
  };

  // ─── One-click data self-heal ────────────────────────────────────
  // Converts legacy `orders.user_id` stored as BSON ObjectId into string
  // form (the Sprint-20 fix). Idempotent — safe to click multiple times.
  // Needed on any environment that has pre-fix orders in Mongo.
  const [healing, setHealing] = useState(false);
  const handleSelfHeal = async () => {
    if (!window.confirm(
      "Run legacy-order data migration?\n\n" +
      "This converts `orders.user_id` from ObjectId to string for any historical " +
      "rows (the Razorpay regression fix). Safe to re-run — if already healthy " +
      "it's a no-op."
    )) return;
    setHealing(true);
    try {
      const { data } = await api.post("/admin/migrate/user-ids-to-string");
      toast.success(
        data.converted > 0
          ? `Healed ${data.converted} order${data.converted === 1 ? "" : "s"}. Admin/customer order pages will now load correctly.`
          : "Already healthy — no rows needed converting."
      );
      load();
    } catch (e) {
      toast.error("Migration failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setHealing(false);
    }
  };

  const summary = data?.summary || {};
  const rows = data?.rows || [];
  const costSplit = data?.cost_split || [];

  // ─── Derived chart shapes ─────────────────────────────────────────
  const trend = useMemo(() => (summary.trend || []).map((t) => ({
    date: (t.day || "").slice(5),
    Revenue: t.revenue,
    Cost: t.cost,
    Profit: t.profit,
  })), [summary.trend]);

  const paymentSplit = useMemo(() => {
    const m = summary.by_payment_method || {};
    return Object.entries(m)
      .filter(([, v]) => v > 0)
      .map(([k, v]) => ({
        name: PAYMENT_LABELS[k] || k,
        value: v,
        fill: METHOD_COLORS[k] || "#94a3b8",
      }));
  }, [summary.by_payment_method]);

  const costSplitForChart = useMemo(() => costSplit
    .filter((c) => c.value > 0)
    .map((c, i) => ({ ...c, fill: COST_SPLIT_COLORS[i % COST_SPLIT_COLORS.length] })),
  [costSplit]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-[#FF6B00]" />
      </div>
    );
  }

  // ─── KPI cards (summary) ──────────────────────────────────────────
  const kpis = [
    { icon: IndianRupee, label: "Total Revenue", value: fmtINR(summary.total_revenue), sub: `${summary.delivered_count || 0} recognised orders`, accent: "green", testId: "kpi-total-revenue" },
    { icon: ArrowDown, label: "Total Agent Payouts", value: fmtINR(summary.total_agent_payouts), sub: `FM ${fmtINR(summary.total_first_mile_payouts)} · LM ${fmtINR(summary.total_last_mile_payouts)}`, accent: "red", testId: "kpi-agent-payouts" },
    { icon: Truck, label: "Line-haul / Courier", value: fmtINR(summary.total_line_haul_cost), sub: "Hub-routed only", accent: "blue", testId: "kpi-linehaul" },
    { icon: Package, label: "Packing Cost", value: fmtINR(summary.total_packing_cost), sub: "Materials passthrough", accent: "purple", testId: "kpi-packing" },
    { icon: Briefcase, label: "Hub Handling", value: fmtINR(summary.total_hub_handling_cost), sub: "Origin + dest hubs", accent: "slate", testId: "kpi-hub-handling" },
    { icon: TrendingUp, label: "Net Profit", value: fmtINR(summary.total_net_profit), sub: "Platform margin", accent: "green", testId: "kpi-net-profit" },
    { icon: AlertTriangle, label: "Pending COD", value: fmtINR(summary.pending_cod_collection), sub: "To be collected", accent: "amber", testId: "kpi-pending-cod" },
    { icon: Wallet, label: "Pending Agent Payout", value: fmtINR(summary.pending_agent_payout), sub: `${summary.in_flight_count || 0} in-flight`, accent: "amber", testId: "kpi-pending-payout" },
  ];

  return (
    <div className="space-y-6" data-testid="admin-revenue-page">
      {/* ─── Header ──────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 animate-fadeInUp">
        <div>
          <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-slate-900">Revenue & Profit Analytics</h1>
          <p className="text-sm text-slate-500 mt-1">
            Money-flow per order — revenue, costs (agent payouts, line-haul, packing, hub handling), and net profit.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={handleSelfHeal}
            disabled={healing}
            className="rounded-xl border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100 text-sm"
            data-testid="self-heal-migrate-btn"
          >
            {healing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Wrench className="h-4 w-4 mr-1.5" />}
            {healing ? "Healing…" : "Heal Legacy Orders"}
          </Button>
          <Button variant="outline" onClick={() => handleExport("csv")} className="rounded-xl border-slate-200 text-sm" data-testid="export-revenue-csv">
            <Download className="h-4 w-4 mr-1.5" /> CSV
          </Button>
          <Button variant="outline" onClick={() => handleExport("pdf")} className="rounded-xl border-slate-200 text-sm" data-testid="export-revenue-pdf">
            <FileText className="h-4 w-4 mr-1.5" /> PDF
          </Button>
        </div>
      </div>

      {/* ─── Filters ────────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4 md:p-5 animate-fadeInUp" data-testid="revenue-filters">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
          <div>
            <label className="text-xs font-medium text-slate-500">Start date</label>
            <Input type="date" value={filters.start_date} onChange={(e) => setFilters({ ...filters, start_date: e.target.value })} className="rounded-xl text-sm mt-1" data-testid="filter-start-date" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500">End date</label>
            <Input type="date" value={filters.end_date} onChange={(e) => setFilters({ ...filters, end_date: e.target.value })} className="rounded-xl text-sm mt-1" data-testid="filter-end-date" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500">Payment method</label>
            <select value={filters.payment_method} onChange={(e) => setFilters({ ...filters, payment_method: e.target.value })} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm mt-1 bg-white" data-testid="filter-payment-method">
              <option value="all">All methods</option>
              <option value="cod">COD</option>
              <option value="wallet">Wallet</option>
              <option value="online">Online / Razorpay</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500">Status</label>
            <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm mt-1 bg-white" data-testid="filter-status">
              <option value="all">All statuses</option>
              <option value="delivered">Delivered</option>
              <option value="pending">Pending</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500">Zone</label>
            <select value={filters.zone} onChange={(e) => setFilters({ ...filters, zone: e.target.value })} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm mt-1 bg-white" data-testid="filter-zone">
              <option value="all">All zones</option>
              <option value="A">Zone A — Same City</option>
              <option value="B">Zone B — Same State</option>
              <option value="C">Zone C — Nearby States</option>
              <option value="D">Zone D — Long Distance</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500">Customer type</label>
            <select value={filters.user_type} onChange={(e) => setFilters({ ...filters, user_type: e.target.value })} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm mt-1 bg-white" data-testid="filter-user-type">
              <option value="all">All</option>
              <option value="b2c">B2C</option>
              <option value="b2b">B2B</option>
            </select>
          </div>
        </div>
      </div>

      {/* ─── Summary KPIs ─────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-4 gap-4 md:gap-5">
        {kpis.map((k) => <StatCard key={k.label} {...k} />)}
      </div>

      {/* ─── Charts row 1: trend + cost split + payment split ──── */}
      <div className="grid lg:grid-cols-3 gap-6">
        <ChartCard title="Revenue / Cost / Profit Trend" subtitle="Per day (recognised revenue)" testId="chart-trend">
          {trend.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-12">No data in this filter range</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={trend}>
                <defs>
                  <linearGradient id="gradRev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#FF6B00" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#FF6B00" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                <Tooltip content={<CostTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Area type="monotone" dataKey="Revenue" stroke="#FF6B00" strokeWidth={2.5} fill="url(#gradRev)" />
                <Area type="monotone" dataKey="Cost" stroke="#ef4444" strokeWidth={2} fillOpacity={0} />
                <Area type="monotone" dataKey="Profit" stroke="#16a34a" strokeWidth={2} fillOpacity={0} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title="Cost Split" subtitle="Where the money goes" testId="chart-cost-split">
          {costSplitForChart.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-12">No data</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={costSplitForChart} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={(e) => `${Math.round((e.percent || 0) * 100)}%`}>
                  {costSplitForChart.map((c) => <Cell key={c.name} fill={c.fill} />)}
                </Pie>
                <Tooltip content={<CostTooltip />} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title="Payment Method Split" subtitle="Recognised revenue by channel" testId="chart-payment-split">
          {paymentSplit.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-12">No data</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={paymentSplit} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={(e) => `${Math.round((e.percent || 0) * 100)}%`}>
                  {paymentSplit.map((p) => <Cell key={p.name} fill={p.fill} />)}
                </Pie>
                <Tooltip content={<CostTooltip />} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>

      {/* ─── Order-wise Revenue & Cost table ─────────────────── */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden animate-fadeInUp">
        <div className="px-5 md:px-6 py-4 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 className="font-['Outfit'] text-base font-semibold text-slate-900 flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-[#FF6B00]" /> Order-wise Money Flow
            </h2>
            <p className="text-xs text-slate-400 mt-0.5">{rows.length} order{rows.length === 1 ? "" : "s"} matching filters</p>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <CreditCard className="h-3.5 w-3.5 text-slate-400" />
            <span className="text-slate-500">All amounts read from stored order data — no recompute</span>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm" data-testid="revenue-orders-table">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left">Order ID</th>
                <th className="px-4 py-3 text-left">Customer</th>
                <th className="px-4 py-3 text-left">Method</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-right">Order Total</th>
                <th className="px-4 py-3 text-right">Agent Payout</th>
                <th className="px-4 py-3 text-right">Line-haul</th>
                <th className="px-4 py-3 text-right">Packing</th>
                <th className="px-4 py-3 text-right">Hub Handling</th>
                <th className="px-4 py-3 text-right">Net Profit</th>
                <th className="px-4 py-3 text-left">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.length === 0 && (
                <tr>
                  <td colSpan={11} className="px-4 py-12 text-center text-slate-400">No orders match the current filters.</td>
                </tr>
              )}
              {rows.map((r) => {
                const profitClass = r.net_profit >= 0 ? "text-emerald-600" : "text-red-600";
                return (
                  <tr key={r.tracking_id} className="hover:bg-slate-50/60 transition-colors" data-testid={`revenue-row-${r.tracking_id}`}>
                    <td className="px-4 py-3 font-mono text-xs text-slate-700">{r.tracking_id}</td>
                    <td className="px-4 py-3 text-slate-700">
                      <div className="font-medium">{r.customer_name || "—"}</div>
                      <div className="text-[11px] text-slate-400">{r.user_type?.toUpperCase()} · {r.zone || "—"}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-md border text-[11px] font-medium ${PAYMENT_BADGE[r.payment_method] || "bg-slate-50 text-slate-600 border-slate-200"}`}>
                        {PAYMENT_LABELS[r.payment_method] || r.payment_method}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-md border text-[11px] font-medium ${STATUS_BADGE[r.status] || "bg-slate-50 text-slate-600 border-slate-200"}`}>
                        {(r.status || "").replace(/_/g, " ")}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-slate-900">{fmtINR(r.order_total)}</td>
                    <td className="px-4 py-3 text-right text-slate-700">
                      <div>{fmtINR(r.agent_payout)}</div>
                      <div className="text-[11px] text-slate-400">FM {fmtINR(r.first_mile_payout)} · LM {fmtINR(r.last_mile_payout)}</div>
                    </td>
                    <td className="px-4 py-3 text-right text-slate-700">{fmtCost(r.line_haul_cost)}</td>
                    <td className="px-4 py-3 text-right text-slate-700">{fmtCost(r.packing_cost)}</td>
                    <td className="px-4 py-3 text-right text-slate-700">{fmtCost(r.hub_handling_cost)}</td>
                    <td className={`px-4 py-3 text-right font-semibold ${profitClass}`}>{fmtINR(r.net_profit)}</td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{(r.created_at || "").slice(0, 10)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ─── Top agents footnote ────────────────────────────── */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 animate-fadeInUp">
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <Users className="h-3.5 w-3.5" />
          <span>
            Recognised revenue is counted once an order is delivered <em>or</em> fully paid online. COD orders pending collection
            are tracked separately in the <strong>Pending COD</strong> KPI and do not yet count towards revenue.
          </span>
        </div>
      </div>
    </div>
  );
}
